import { useMemo } from 'react';
import { useTicketsList, useAssignees } from '../hooks/useTicketsApi';
import { useAgentStatus } from '../hooks/useSettingsApi';
import { useOpsStats } from '../hooks/useAuditApi';
import { useAuth } from '../contexts/AuthContext';
import { useTicketFilters, toQueryString, toUrlString } from '../hooks/useTicketFilters';
import TicketFilterBar, { columnFilterChips } from '../components/TicketFilterBar';
import { rememberBoardUrl } from '../components/Layout';
import TicketTable, { REVIEW_DEFAULT_SORT, REVIEW_HIDDEN_COLUMNS } from '../components/TicketTable';
import { LoadError } from '../components/ui/LoadError';
import { LoadingAnnouncement } from '../components/ui/Skeleton';

/**
 * THE REVIEW COCKPIT (P22, Roman's decision 8)
 *
 * =========================================================================
 * WHY A SECOND BOARD AND NOT A FILTER ON THE FIRST
 * =========================================================================
 * The 2026-08-31 review meeting was, in practice, four people reading a
 * 46-row ticket table out loud looking for the ones that had stopped. That is
 * the daily work of the pilot, and it had no page.
 *
 * It is a ROUTE, not a new permission. `/review` renders under the existing
 * `/tickets` grant (`RequirePage page="/tickets"`), so no migration, no matrix
 * row, and nobody's access changes — App.tsx's gating is keyed on the page
 * string, not on the path.
 *
 * =========================================================================
 * WHAT COUNTS AS "IN REVIEW"
 * =========================================================================
 * `Status='NeedsReview' OR ReviewReason IS NOT NULL` — sent as `?review=1` and
 * evaluated in SQL. Not `Status='NeedsReview'`, which was the obvious guess and
 * is wrong four times over on the live data (2026-09-01):
 *
 *   DRA-2026-00003   New              AuthorizationRequestExpired
 *   DRA-2026-00016   Closed           DraftNoTemplate
 *   DRA-2026-00025   AwaitingApproval CustomerAmbiguous; AuthCustomerUnresolved
 *   DRA-2026-00045   NeedsReview      (no reason at all)
 *
 * The first three carry a question nobody is answering because no queue shows
 * them; the fourth is a ticket in the review status whose reason a hand status
 * change wiped. All four are exactly the tickets that get lost, so both halves
 * of the OR are load-bearing.
 *
 * =========================================================================
 * AND WHAT DOES NOT COUNT (P31, Roman's decision 3 of 2026-09-02)
 * =========================================================================
 * `DRA-2026-00016` is in that list above, and it should never have been: it was
 * DELIVERED on 2026-08-25 and has carried `DraftNoTemplate` at the top of this
 * board ever since. A question on a finished ticket cannot be answered by
 * anybody, so it is not work - it is debris left by a close that used to clear
 * the reason only while the ticket sat in `NeedsReview`.
 *
 * So review is OPEN work: the predicate excludes `Closed` and `Cancelled`, every
 * close now clears the reason, and the one control below reaches the debris on
 * purpose. Decision 8 of 2026-09-01 is revised in exactly two points - no status
 * filter here (the Status column keeps its badge and loses its funnel), and no
 * closed tickets unless they are asked for.
 *
 * The page is also the QUEUE now rather than a list of it: it opens waiting
 * longest first, carries a `Waiting` column, hands the ticket screen the order
 * so a reviewer can walk it without coming back here, and exports what is on the
 * screen.
 */
export default function ReviewBoard() {
  const { filters, view, setFilters, toggleIn, setOne, setSort, setColumnFilter, clear, activeCount } = useTicketFilters();
  const { user } = useAuth();

  const query = useMemo(() => toQueryString(filters, true), [filters]);
  const { data: result, isLoading, isError, error, errorUpdatedAt, refetch } = useTicketsList(query);
  const tickets = result?.data;

  // The row click hands this to the ticket screen, which re-reads it to walk the
  // queue. `toUrlString`, not `toQueryString`: the sort is half of what "next"
  // means, and only the address-bar form carries it.
  const origin = useMemo(() => `/review?${toUrlString(filters, view)}`, [filters, view]);
  // Task 9 (K9) - see TicketBoard. `/review` keeps its own key: the two boards
  // are different questions, and a reviewer's queue filter is not the archive's.
  rememberBoardUrl('/review', origin);
  const { data: assignees } = useAssignees();
  const { data: agent } = useAgentStatus();
  const staleAfter = agent?.staleTicketDays ?? null;

  /*
   * Task 3. `ticketsInReview` is the one number that is GUARANTEED to be the
   * total of this page: it is counted server-side through
   * `shared/review-predicate.ts`, the very module `tickets/list.ts` evaluates for
   * `?review=1` — P31 made it one module precisely so a counter could not say 32
   * above a page showing 31, and P37a added the derived third disjunct to both at
   * once. So the empty cell can state it without a second query and without
   * risking a number the page would contradict.
   *
   * It does NOT follow `includeClosed`, and that is why the sentence says "open".
   */
  const { data: stats } = useOpsStats();

  return (
    // See TicketBoard: the flex column is what gives the table a height to
    // stick its header to (P37, audit S1-08).
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Review</h1>
          <p className="mt-1 text-sm text-gray-500">
            Every ticket the agent stopped on, plus every ticket still carrying an open question — including the ones
            that have moved on in status and kept the question. Finished tickets are not work and are left out.{' '}
            {/* The same correction as TicketBoard's headline (design audit
                S1-03): the count is filtered, so under a filter it may not be
                called "waiting" — that would say the queue is empty when it is
                only hidden. No view buttons here, so any active filter counts. */}
            {tickets ? `${tickets.length} ${activeCount > 0 ? 'matching this filter' : 'waiting'}.` : ''}
          </p>
        </div>
        {/* Not a filter, and deliberately not a chip: it WIDENS the queue into
            tickets nobody can act on. Since P31 every close clears the reason, so
            what it reaches is the debris of before - one ticket on 2026-09-02. */}
        <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={filters.includeClosed}
            onChange={e => setOne('includeClosed', e.target.checked)}
          />
          <span title="Closed and cancelled tickets whose review reason was never cleared. Since 2026-09-02 no close leaves one behind, so this reaches older debris only.">
            Closed with a leftover question
          </span>
        </label>
      </div>

      <TicketFilterBar
        filters={filters}
        toggleIn={toggleIn}
        setOne={setOne}
        clear={clear}
        activeCount={activeCount}
        extraChips={columnFilterChips(view.columnFilters, setColumnFilter)}
      />

      {/* P38, task 2 — and it matters most here: `/review` is where somebody
          works for an hour with a sort and six column filters, and a reload was
          the only recovery. */}
      {isError && <LoadError what="The review queue" error={error} at={errorUpdatedAt} onRetry={refetch} />}
      {isLoading && <LoadingAnnouncement what="the review queue" />}

      {!isError && (
        <TicketTable
          isLoading={isLoading}
          tickets={tickets ?? []}
          total={result?.pagination?.total}
          staleAfter={staleAfter}
          showReviewReason
          storageKey="dra-review-col-widths"
          // Doc Type is SDS on 54 of 54 tickets, Priority is Normal on 50, and a
          // Due date is set on 4 - three columns of width spent on nothing. The
          // chooser still offers them; this is a starting set, not a removal.
          defaultHiddenColumns={REVIEW_HIDDEN_COLUMNS}
          defaultSort={REVIEW_DEFAULT_SORT}
          hideStatusFilter
          origin={origin}
          exportFileName="review"
          emptyMessage={activeCount > 0 ? 'No ticket in review matches these filters.' : 'Nothing is waiting on a person.'}
          // Task 3, and the honest version of it: the queue's own total is the one
          // number this page can state without a second query, because with no
          // filter beyond the queue itself `tickets.length` IS the queue. Under a
          // filter it is not, so the sentence says what to do instead of inventing
          // a figure.
          emptyTotalLabel={
            activeCount > 0 && stats
              ? `There ${stats.ticketsInReview === 1 ? 'is' : 'are'} ${stats.ticketsInReview} open in the queue.`
              : undefined
          }
          filters={filters}
          setFilters={setFilters}
          setOne={setOne}
          sort={view.sort}
          onSortChange={setSort}
          columnFilters={view.columnFilters}
          setColumnFilter={setColumnFilter}
          assignees={assignees ?? []}
          currentUserEmail={user?.email}
        />
      )}
    </div>
  );
}
